/**
 * callLog.ts — Sends "Missed Call" or "Call Ended" messages to a chat channel.
 *
 * The caller's browser is responsible for sending these messages because
 * the caller is always online when the call terminates. This handles
 * the offline-recipient scenario where the backend webhook may never
 * receive a call.session_ended event.
 */

import type { StreamChat } from "stream-chat";

// ─── Same deterministic hash used by UserListModal for DM channel IDs ───
function shortHash(input: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return ((h2 >>> 0) * 0x100000000 + (h1 >>> 0)).toString(16).padStart(16, "0");
}

/**
 * Resolves the DM channel ID for two users (same logic as UserListModal).
 */
export function getDmChannelId(userId1: string, userId2: string): string {
  const sortedIds = [userId1, userId2].sort();
  return `dm-${shortHash(sortedIds.join("-"))}`;
}

export interface SendCallLogOptions {
  chatClient: StreamChat;
  myUserId: string;
  otherUserId: string;
  otherUserName: string;
  /** true = the call was answered and both parties connected */
  wasConnected: boolean;
  /** Duration string like "02:15", only relevant for completed calls */
  duration?: string;
  /** Optional: pre-resolved channel ID (skips the hash lookup) */
  channelId?: string;
}

/**
 * Sends a call-log message to the DM channel between the two users.
 *
 * - Missed call:  "📞 Missed call from [callerName]"
 * - Call ended:   "📞 Call ended · Duration: MM:SS"
 *
 * Messages include `call_log: true` custom data so the CustomCallMessage
 * component can render them as centered pills instead of regular bubbles.
 *
 * Uses fire-and-forget — errors are logged but never thrown.
 */
export async function sendCallLogMessage({
  chatClient,
  myUserId,
  otherUserId,
  otherUserName,
  wasConnected,
  duration = "00:00",
  channelId,
}: SendCallLogOptions): Promise<void> {
  try {
    const resolvedChannelId = channelId || getDmChannelId(myUserId, otherUserId);
    const channel = chatClient.channel("messaging", resolvedChannelId);

    // Ensure the channel is watchable (no-op if already watched)
    await channel.watch();

    const callerName = chatClient.user?.name || myUserId;

    if (wasConnected) {
      await channel.sendMessage({
        text: `📞 Call ended · Duration: ${duration}`,
        call_log: true,
        call_log_data: {
          call_log_type: "call_summary",
          call_type: "audio",
          caller_id: myUserId,
          caller_name: callerName,
          call_duration_formatted: duration,
          participants: [callerName, otherUserName],
        },
      } as any);
    } else {
      await channel.sendMessage({
        text: `📞 Missed call from ${callerName}`,
        call_log: true,
        call_log_data: {
          call_log_type: "missed_call",
          call_type: "audio",
          caller_id: myUserId,
          caller_name: callerName,
          participants: [],
        },
      } as any);
    }

    console.log(
      `[CallLog] Sent ${wasConnected ? "call_summary" : "missed_call"} to channel ${resolvedChannelId}`,
    );
  } catch (err) {
    console.error("[CallLog] Failed to send call log message:", err);
  }
}
